import t from 'react-translate';
import { pick } from 'underscore';
import { useCallback, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { getCourseAliases } from 'redux/selectors/course';
import { ComponentType, LectureComponent, RichTextType } from 'redux/schemas/models/lecture-component';
import { NovoAIItemType } from 'redux/schemas/models/lecture-page';
import { innerPayloadKey } from 'redux/schemas/models/activity';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';

const useAiSuggestComponents = () => {
  const aliases = useSelector((state) => getCourseAliases(state));
  const params = useLecturePageParams();

  const componentTextMapping = useMemo(() => ({
    [ComponentType.PROFILE_COMPLETION]: t.LECTURE_PAGES.COMPONENTS.PROFILE_COMPLETION.DESCRIPTION(),
    [ComponentType.MEET_AND_GREET]: t.LECTURE_PAGES.COMPONENTS.MEET_AND_GREET.DESCRIPTION(),
    [ComponentType.TOPIC]: t.LECTURE_PAGES.COMPONENTS.DISCUSSION.DESCRIPTION(),
    [ComponentType.QUIZ]: t.LECTURE_PAGES.COMPONENTS.QUIZ.DESCRIPTION(),
    [ComponentType.PROGRESSIVE_QUIZ]: t.LECTURE_PAGES.COMPONENTS.QUIZ.DESCRIPTION(),
    [ComponentType.POLL]: t.LECTURE_PAGES.COMPONENTS.POLLS.DESCRIPTION(),
    [ComponentType.EXERCISE]: t.LECTURE_PAGES.COMPONENTS.EXERCISE.DESCRIPTION(aliases.assignmentAliases),
    [ComponentType.PUBLIC_PEER_EVALUATION]: t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.ASSIGNMENT.PUBLIC(),
    [ComponentType.SURVEY]: t.LECTURE_PAGES.COMPONENTS.SURVEY.DESCRIPTION(),
  }), [aliases.assignmentAliases]);

  const componentIconMapping = useMemo(() => ({
    [ComponentType.PROFILE_COMPLETION]: 'profile',
    [ComponentType.MEET_AND_GREET]: 'meet-your-peers',
    [ComponentType.TOPIC]: 'conversations',
    [ComponentType.QUIZ]: 'quiz',
    [ComponentType.POLL]: 'polls',
    [ComponentType.EXERCISE]: 'assignments',
    [ComponentType.PUBLIC_PEER_EVALUATION]: 'comments',
    [ComponentType.SURVEY]: 'survey',
  }), []);

  // BE assumes question set components have similar payload structure
  const getQuestionComponentPayload = (lc) => {
    const typekey = innerPayloadKey[lc.type];

    return {
      ...pick(lc, ['trueType', 'type']),
      [typekey]: {
        ...pick(lc[typekey], [
          'title', 'expirationDate', 'graded', 'hardDeadline',
          'isTodo', 'maximumAttempts',
        ]),
        questionsAttributes: lc[typekey].questions.map((question) => ({
          ...pick(question, ['questionText', 'type']),
          responseOptionsAttributes: question.responseOptions.map((option) => (
            pick(option, ['optionContent', 'isCorrect', 'explanation']))),
        })),
      },
    };
  };

  /**
   * The payload is being constructed to match the existing component
   * creation payload structure, using the data from the AI-suggested components.
   * @param component AI-suggested component
   * @returns constructed payload object
   */
  const getComponentPayload = useCallback((component, aiActionType) => {
    switch (component.trueType) {
      // We are adding header LC as part of the Meet & Greet component
      case ComponentType.HEADER_STYLE1:
        return {
          ...pick(component, 'trueType', 'type', 'viewOptions', 'content'),
        };
      case ComponentType.MEET_AND_GREET:
        return {
          ...pick(component, 'trueType', 'type'),
          meetAndGreet: pick(
            component.meetAndGreet,
            'answerableId', 'answerableType', 'defaultProfileQuestion',
            'photosRequired', 'sortBy',
          ),
        };
      case ComponentType.QUIZ:
        /**
         * We only need to generate the payload when creating through the
         * ‘All Suggestions’ flow. For a single quiz suggestion, the component
         * already contains the required payload data, So, we don’t need to
         * construct the payload in that case.
         */
        if (aiActionType === NovoAIItemType.SUGGEST_ACTIVITIES) {
          return getQuestionComponentPayload(component);
        }

        return component;
      case ComponentType.POLL:
      case ComponentType.SURVEY:
        return getQuestionComponentPayload(component);
      case ComponentType.TOPIC:
        return {
          ...pick(component, 'trueType', 'type'),
          topic: {
            aiAction: aiActionType,
            ...(component.topic.forumId
              ? { forumId: component.topic.forumId }
              : { forumTitle: component.topic.forum.title }),
            ...pick(component.topic, 'title', 'body', 'starterId', 'expandedOnLoad'),
          },
        };
      case ComponentType.EXERCISE:
        return {
          ...pick(component, 'trueType', 'type'),
          exercise: {
            ...pick(
              component.exercise,
              'title', 'name', 'description', 'formedByStudents', 'privacySetting',
              'isTodo', 'hardDeadline', 'deadline', 'teamType', 'template',
            ),
          },
        };
      case ComponentType.PUBLIC_PEER_EVALUATION:
        return {
          ...pick(component, 'trueType', 'type'),
          peerEvaluation: {
            ...pick(
              component.peerEvaluation,
              'exerciseId', 'hardDeadline', 'isTodo', 'targetGoal', 'title',
            ),
            description: `<div class="froala-style-regular">${component.peerEvaluation.description}</div>`,
          },
        };
      // Summary and key-takeaway components
      case RichTextType.FULL:
        return {
          ...pick(component, 'trueType', 'type', 'viewOptions', 'content'),
        };
      case ComponentType.TEXT_WITH_IMAGE_BKG:
      case ComponentType.TEXT_WITH_IMAGE_SIDE:
      case ComponentType.TEXT_WITH_IMAGE_TOP:
        return {
          ...pick(component, 'trueType', 'type', 'viewOptions', 'content'),
          picture: null,
        };
      default:
        return component;
    }
  }, []);

  /**
   * Builds the complete payload for creating a new lecture component from AI-generated data.
   * This function consolidates the component data with metadata required by the backend API.
   *
   * @param lc - The AI-generated lecture component containing the activity data
   * @param index - The position index where the component should be inserted in the page
   * @param itemType - The NovoAI item type that triggered this creation
   * @returns A complete payload object ready for the addNewComponent API call
   *
   * @example
   * const payload = buildComponentCreationPayload(quizComponent, 0, NovoAIItemType.QUIZ);
   * dispatch(addNewComponent(payload));
   */
  const buildComponentCreationPayload = useCallback((lc: LectureComponent, index: number, itemType: NovoAIItemType) => ({
    index,
    lecturePageId: params.lecturePageId,
    catalogId: params.catalogId,
    lectureComponent: {
      aiProperties: { ai_action: itemType },
      ...getComponentPayload(lc, itemType),
    },
    isAiSuggested: true,
  }), [getComponentPayload, params.catalogId, params.lecturePageId]);

  return {
    componentTextMapping,
    componentIconMapping,
    buildComponentCreationPayload,
  };
};

export default useAiSuggestComponents;
